import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FloatField, FloatWrapper } from "@/components/ui/FormField";
import { EmployeeCombobox } from "@/components/ui/EmployeeCombobox";
import { FilePlus, GitBranch, Loader2 } from "lucide-react";
import {
  apiCreateHRDocument,
  apiGetDocumentTemplates,
  apiGetDocumentChainTemplates,
  apiSaveDocumentChainTemplate,
  apiStartDocumentChain,
  DOCUMENT_TYPES,
  EXPIRY_DOCUMENT_TYPES,
} from "@/lib/api";
import { getDocumentTypeForTemplate } from "@/lib/document-types";
import type {
  HRDocument,
  DocumentTemplate,
  DocumentType,
  DocumentChainStep,
  DocumentChainTemplate,
} from "@/lib/api";
import { toast } from "sonner";
import { DocumentChainBuilder } from "./DocumentChainBuilder";

interface Employee {
  id: number;
  name: string;
  jobTitle?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employees: Employee[];
  onDocumentCreated: (doc: HRDocument) => void;
  defaultEmployeeId?: number;
}

const MANUAL_TEMPLATE_VALUE = "__manual__";

export function RequestDocumentModal({
  open,
  onOpenChange,
  employees,
  onDocumentCreated,
  defaultEmployeeId,
}: Props) {
  const [employeeId, setEmployeeId] = useState<string>(
    defaultEmployeeId ? String(defaultEmployeeId) : "",
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [documentName, setDocumentName] = useState("");
  const [documentType, setDocumentType] = useState<DocumentType | "">("");
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  // Chain state
  const [steps, setSteps] = useState<DocumentChainStep[]>([]);
  const [chainTemplates, setChainTemplates] = useState<DocumentChainTemplate[]>([]);

  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ["document-templates"],
    queryFn: apiGetDocumentTemplates,
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setEmployeeId(defaultEmployeeId ? String(defaultEmployeeId) : "");
      apiGetDocumentChainTemplates()
        .then(setChainTemplates)
        .catch(() => {});
    }
  }, [defaultEmployeeId, open]);

  const reset = () => {
    setEmployeeId(defaultEmployeeId ? String(defaultEmployeeId) : "");
    setSelectedTemplateId("");
    setDocumentName("");
    setDocumentType("");
    setSteps([]);
  };

  const handleTemplateChange = (value: string) => {
    if (value === MANUAL_TEMPLATE_VALUE) {
      setSelectedTemplateId("");
      return;
    }
    setSelectedTemplateId(value);
    const template = templates.find((item: DocumentTemplate) => String(item.id) === value);
    if (template) {
      setDocumentName(template.name);
      setDocumentType(getDocumentTypeForTemplate(template.templateType));
    }
  };

  const handleOpenChange = (val: boolean) => {
    if (!loading) {
      if (!val) reset();
      onOpenChange(val);
    }
  };

  const handleSaveChainTemplate = async (name: string, chainSteps: DocumentChainStep[]) => {
    try {
      const saved = await apiSaveDocumentChainTemplate(name, chainSteps);
      setChainTemplates((prev) => [saved, ...prev]);
      toast.success(t("documents.chain.templateSaved", { name }));
    } catch (err: any) {
      toast.error(t("documents.chain.templateSaveFailed"), { description: err.message });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!employeeId || !documentName.trim()) return;

    setLoading(true);
    try {
      const doc = await apiCreateHRDocument({
        employeeId: Number(employeeId),
        documentName: documentName.trim(),
        documentType: documentType || undefined,
        templateId: selectedTemplateId ? Number(selectedTemplateId) : undefined,
      });

      if (steps.length > 0) {
        try {
          await apiStartDocumentChain(doc.id, steps);
        } catch {
          toast.warning(t("documents.chain.chainStartFailed"));
        }
      }

      onDocumentCreated(doc);
      toast.success(t("documents.messages.requestCreated"), {
        description: t("documents.messages.requestCreatedDesc", {
          name: doc.documentName,
        }),
      });
      handleOpenChange(false);
    } catch (err: any) {
      toast.error(t("documents.messages.requestFailed"), {
        description: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedTemplate = templates.find(
    (template: DocumentTemplate) => String(template.id) === selectedTemplateId,
  );
  const isValid = Boolean(employeeId && documentName.trim());

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[900px] p-0 gap-0 overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 bg-muted/30 border-b">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2.5 text-base font-semibold">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <FilePlus className="h-4 w-4 text-primary" />
              </div>
              {t("documents.requestTitle")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mt-1.5">
            {t("documents.requestDesc")}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="flex flex-row min-h-0" style={{ maxHeight: "65vh" }}>
            {/* Left panel — document fields */}
            <div className="flex-1 px-6 py-4 border-r overflow-y-auto space-y-4">
              {/* Employee */}
              <FloatWrapper label={t("documents.employeeLabel")} required>
                <EmployeeCombobox
                  employees={employees}
                  value={employeeId}
                  onChange={setEmployeeId}
                  placeholder={t("documents.selectEmployee")}
                  disabled={loading}
                />
              </FloatWrapper>

              {/* Template */}
              <FloatWrapper label={t("documents.templateLabel")}>
                <Select
                  value={selectedTemplateId || MANUAL_TEMPLATE_VALUE}
                  onValueChange={handleTemplateChange}
                  disabled={loading || templatesLoading}
                >
                  <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4">
                    <SelectValue placeholder={t("documents.selectTemplate")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={MANUAL_TEMPLATE_VALUE}>
                      {t("documents.manualTemplate")}
                    </SelectItem>
                    {templates.map((template: DocumentTemplate) => (
                      <SelectItem key={template.id} value={String(template.id)}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!templatesLoading && templates.length === 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("documents.noTemplatesForRequest")}
                  </p>
                )}
                {selectedTemplate && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {selectedTemplate.fileKey && !selectedTemplate.contentText
                      ? t("documents.templates.pdfFileTemplate")
                      : t("documents.templateWillPopulate")}
                  </p>
                )}
              </FloatWrapper>

              {/* Document type */}
              <FloatWrapper label={t("documents.documentTypeLabel")}>
                <Select value={documentType} onValueChange={(v) => setDocumentType(v as DocumentType)} disabled={loading}>
                  <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4">
                    <SelectValue placeholder={t("documents.selectType")} />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(`documents.type.${type.replace(/\s+/g, "")}`, { defaultValue: type })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FloatWrapper>

              {/* Document name */}
              <FloatField
                label={t("documents.documentName")}
                required
                placeholder={t("documents.documentNamePlaceholder")}
                value={documentName}
                onChange={(v) => setDocumentName(v as string)}
              />

              {/* GDPR notice for identity document types */}
              {documentType !== "" && EXPIRY_DOCUMENT_TYPES.includes(documentType as any) && (
                <div className="px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-400">
                  ⚠️ {t("documents.gdprNotice")}
                </div>
              )}
            </div>

            {/* Right panel — chain builder */}
            <div className="w-[360px] shrink-0 px-5 py-4 bg-muted/20 overflow-y-auto flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <GitBranch className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">{t("documents.chain.panelTitle")}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                {t("documents.chain.panelDesc")}
              </p>
              <div className="flex-1 min-h-0">
                <DocumentChainBuilder
                  steps={steps}
                  onChange={setSteps}
                  templates={chainTemplates}
                  onSaveTemplate={handleSaveChainTemplate}
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 pb-6 pt-4 border-t bg-muted/20 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={loading}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={loading || !isValid}
              className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 border-0"
            >
              {loading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  {t("documents.sending")}
                </>
              ) : (
                <>
                  <FilePlus className="h-3.5 w-3.5 mr-1.5" />
                  {t("documents.sendRequest")}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
